import { useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Badge, Typography, Card, CardContent, Stack } from "@altimateai/lego";
import { NumberFormatter } from "@uicore";
import { useAppState } from "@/modules/app/useAppContext";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { FilterType, SortDirection, parseSortDirection } from "@/components/tables/types";
import { useDatabricksUsers, useDatabricksUserFilters } from "../hooks";
import { DatabricksUser } from "../types";
import { formatDuration, formatSuccessRate, getSuccessRateBadgeColor } from "../helpers";
import { formatBytes } from "@/helpers/utils";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import dayjs from "dayjs";

const USER_TYPE_LABELS: Record<string, string> = {
  USER: "User",
  SERVICE_PRINCIPAL: "Service Principal",
};
const USER_TYPE_OPTIONS = Object.entries(USER_TYPE_LABELS).map(([value, label]) => ({
  label,
  value,
}));

const FILTER_PORTAL_ID = "databricks-users-filter-container";
const SAVED_FILTERS_PORTAL_ID = "databricks-users-saved-filters-container";

interface Props {
  startDate: string;
  endDate: string;
}

const DatabricksUserList = ({ startDate, endDate }: Props) => {
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "20", 10);
  const sortAttribute = searchParams.get("sortAttribute") || "total_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const userNameFilter = searchParams.getAll("userName");
  const userTypeFilter = searchParams.get("user_types") || "";

  const { data: filterOptions } = useDatabricksUserFilters({});

  const { data, isFetching } = useDatabricksUsers({
    start_date: startDate,
    end_date: endDate,
    user_names: userNameFilter.length > 0 ? userNameFilter : undefined,
    user_types: userTypeFilter ? [userTypeFilter] : undefined,
    page: page + 1,
    page_size: pageSize,
    sortAttribute,
    sortOrder,
  });

  const handleSortChange = useCallback(
    (attribute: string, direction: SortDirection) => {
      updateSearchParamsByKey({
        sortAttribute: direction ? attribute : undefined,
        sortOrder: direction || undefined,
        page: "0",
      });
    },
    [updateSearchParamsByKey]
  );

  const buildSortConfig = useCallback(
    (attribute: string) => ({
      onChange: (newSort: SortDirection) => handleSortChange(attribute, newSort),
      value: (sortAttribute === attribute ? parseSortDirection(sortOrder) : "") as SortDirection,
    }),
    [handleSortChange, sortAttribute, sortOrder]
  );

  const userNameOptions = useMemo(
    () => (filterOptions?.user_names || []).map(name => ({ label: name, value: name })),
    [filterOptions?.user_names]
  );

  return (
    <Card>
      <CardContent className="al-pt-4">
        <Stack direction="row" align="center" justify="between" className="al-mb-4 al-w-full">
          <div id={FILTER_PORTAL_ID} />
          <div id={SAVED_FILTERS_PORTAL_ID} />
        </Stack>
        <DataTable
          applicableFor={FilterApplicableFor.DATABRICKS_USERS}
          portals={{ filter: FILTER_PORTAL_ID, savedFilters: SAVED_FILTERS_PORTAL_ID }}
          header={[
            {
              id: 1,
              uid: "user_name",
              label: "User Name",
              width: "20%",
              sort: buildSortConfig("user_name"),
              filter:
                userNameOptions.length > 0
                  ? {
                      filterType: FilterType.DROPDOWN,
                      label: "User Name",
                      options: userNameOptions,
                      value: userNameFilter,
                      searchKey: "userName",
                      onChange: (value: string[]) => {
                        updateSearchParamsByKey({ userName: value, page: "0" });
                      },
                    }
                  : undefined,
              render: (item: DatabricksUser) => (
                <Link
                  to={`/queries?userName=${encodeURIComponent(item.user_name)}`}
                  className="al-text-brand hover:al-underline"
                >
                  <Typography weight="medium" className="al-text-inherit">
                    {item.user_name}
                  </Typography>
                </Link>
              ),
            },
            {
              id: 10,
              uid: "user_type",
              label: "User Type",
              width: "16%",
              filter: {
                filterType: FilterType.DROPDOWN_SINGLE,
                label: "User Type",
                options: USER_TYPE_OPTIONS,
                value: userTypeFilter,
                searchKey: "user_types",
                onChange: (value: string) => {
                  updateSearchParamsByKey({ user_types: value || undefined, page: "0" });
                },
              },
              render: (item: DatabricksUser) => (
                <Badge variant="outline">
                  {item.user_type ? (USER_TYPE_LABELS[item.user_type] ?? item.user_type) : "-"}
                </Badge>
              ),
            },
            {
              id: 2,
              uid: "total_cost",
              label: "Query Cost",
              width: "8%",
              sort: buildSortConfig("total_cost"),
              render: (item: DatabricksUser) => (
                <NumberFormatter value={item.total_cost || 0} options={{ currency }} />
              ),
            },
            {
              id: 3,
              uid: "total_dbus",
              label: "DBUs",
              width: "8%",
              sort: buildSortConfig("total_dbus"),
              render: (item: DatabricksUser) => (
                <Typography>{(item.total_dbus || 0).toFixed(2)}</Typography>
              ),
            },
            {
              id: 4,
              uid: "total_queries",
              label: "Queries",
              width: "12%",
              sort: buildSortConfig("total_queries"),
              render: (item: DatabricksUser) => (
                <Stack direction="column">
                  <Typography weight="medium">{item.total_queries.toLocaleString()}</Typography>
                  <Typography size="xs" className="al-text-gray-500">
                    <span className="al-text-green-600">
                      {item.successful_queries.toLocaleString()}
                    </span>
                    {" / "}
                    <span className="al-text-red-600">{item.failed_queries.toLocaleString()}</span>
                  </Typography>
                </Stack>
              ),
            },
            {
              id: 5,
              uid: "success_rate",
              label: "Success Rate",
              width: "10%",
              sort: buildSortConfig("success_rate"),
              render: ({ successful_queries: s, total_queries: t }: DatabricksUser) => (
                <Badge className={getSuccessRateBadgeColor(s, t)}>{formatSuccessRate(s, t)}</Badge>
              ),
            },
            {
              id: 6,
              uid: "avg_duration_ms",
              label: "Avg Duration",
              width: "10%",
              sort: buildSortConfig("avg_duration_ms"),
              render: (item: DatabricksUser) => (
                <Typography>{formatDuration(item.avg_duration_ms)}</Typography>
              ),
            },
            {
              id: 7,
              uid: "total_duration_ms",
              label: "Total Time",
              width: "10%",
              sort: buildSortConfig("total_duration_ms"),
              render: (item: DatabricksUser) => (
                <Typography>{formatDuration(item.total_duration_ms || 0)}</Typography>
              ),
            },
            {
              id: 8,
              uid: "total_read_bytes",
              label: "Data Read",
              width: "10%",
              sort: buildSortConfig("total_read_bytes"),
              render: (item: DatabricksUser) => (
                <Typography>{formatBytes(item.total_read_bytes)}</Typography>
              ),
            },
            {
              id: 9,
              uid: "last_query_time",
              label: "Last Query",
              width: "12%",
              sort: buildSortConfig("last_query_time"),
              render: (item: DatabricksUser) => (
                <Typography size="sm" className="al-text-gray-600">
                  {item.last_query_time
                    ? dayjs(item.last_query_time).format("MMM D, YYYY HH:mm")
                    : "-"}
                </Typography>
              ),
            },
          ]}
          data={{ items: data?.users || [] }}
          isLoading={isFetching}
          minWidth={1300}
        />
        <Paginate
          itemCount={data?.total_count || 0}
          page={page}
          pageSize={pageSize}
          numPages={data?.total_pages || 1}
          onPageClick={newPage => updateSearchParamsByKey({ page: String(newPage) })}
          onPageSizeChange={newSize =>
            updateSearchParamsByKey({ pageSize: String(newSize), page: "0" })
          }
          showPageSize
          isLoading={isFetching}
        />
      </CardContent>
    </Card>
  );
};

export default DatabricksUserList;
